import { Controller } from "@hotwired/stimulus"
import { styleControl, propControl } from "../builder/controls"

// Generated entirely from the schema, so adding a node type gives it an
// inspector for free.
export default class extends Controller {
  static targets = ["panel"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.root.addEventListener("builder:ready", () => this.start(), { once: true })
    this.element.addEventListener("control:refresh", () => this.render())
    if (this.root.builder) this.start()
  }

  disconnect() { this.unsubscribe?.() }

  get builder() { return this.root.builder }
  get store() { return this.builder.store }

  start() {
    this.unsubscribe = this.store.subscribe((event) => {
      if (["select", "document", "structure", "breakpoint"].includes(event.type)) this.render()
    })
    this.render()
  }

  render() {
    const schema = this.builder.schema
    const node = this.store.selected

    if (!node) {
      this.panelTarget.innerHTML =
        `<p class="text-xs text-slate-500">${schema.labels.ui.no_selection}</p>`
      return
    }

    const fragment = document.createDocumentFragment()
    fragment.append(this.header(node, schema))

    const props = schema.props[node.type] || {}
    if (Object.keys(props).length) fragment.append(this.propsSection(node, props, schema))

    ;(schema.styleGroups[node.type] || []).forEach((group) => {
      fragment.append(this.styleGroup(node, group, schema))
    })

    this.panelTarget.replaceChildren(fragment)
  }

  header(node, schema) {
    const wrap = document.createElement("div")
    wrap.className = "mb-3 flex items-center justify-between border-b border-slate-200 pb-2 dark:border-slate-800"

    const title = document.createElement("h2")
    title.className = "text-sm font-semibold"
    title.textContent = schema.labels.types[node.type] || node.type

    const actions = document.createElement("div")
    actions.className = "flex items-center gap-3"

    const duplicate = document.createElement("button")
    duplicate.type = "button"
    duplicate.textContent = schema.labels.ui.duplicate
    duplicate.className = "text-xs text-slate-500 hover:text-cyan-600"
    duplicate.disabled = node.id === "root"
    duplicate.addEventListener("click", () => this.builder.duplicate(node.id))

    const remove = document.createElement("button")
    remove.type = "button"
    remove.textContent = schema.labels.ui.delete
    remove.className = "text-xs text-red-600 hover:underline"
    remove.disabled = node.id === "root"
    remove.addEventListener("click", () => this.store.remove(node.id))

    actions.append(duplicate, remove)
    wrap.append(title, actions)
    return wrap
  }

  section(title) {
    const wrap = document.createElement("section")
    wrap.className = "mb-4"
    const heading = document.createElement("h3")
    heading.className = "mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
    heading.textContent = title
    wrap.append(heading)
    return wrap
  }

  propsSection(node, props, schema) {
    const wrap = this.section(schema.labels.ui.content)
    Object.entries(props).forEach(([key, rule]) => {
      wrap.append(propControl({
        key,
        rule,
        value: this.store.prop(node.id, key),
        labels: schema.labels,
        onChange: (value) => this.store.setProp(node.id, key, value)
      }))
    })
    return wrap
  }

  styleGroup(node, group, schema) {
    const wrap = this.section(schema.labels.groups[group.name] || group.name)

    group.keys.forEach((key) => {
      const definition = schema.styleKeys[key]
      if (!definition) return

      wrap.append(styleControl({
        key,
        definition,
        value: this.store.styleValue(node.id, key),
        labels: schema.labels,
        tokens: schema.tokens,
        onChange: (value) => this.store.setStyle(node.id, key, value)
      }))
    })

    return wrap
  }
}
